/**
 * Module: InProgressFlowsBlock
 * Role: Home horizontal rail for buyer-owned flow slots in progress.
 * Uses: api/active-flows, react-query, react-router, atoms, flow labels, tokens.
 * Used by: screens/HomeScreen/index
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchActiveFlows, type ActiveFlowCard } from '../../api/active-flows';
import { Card, Ic } from '../../components/atoms';
import { formatKopecks } from '../../lib/format';
import { P, S, T } from '../../styles/tokens';
import { flowStepLabel } from '../flow/_labels';

function stateLabel(state: ActiveFlowCard['step_state']): string {
  const labels: Record<ActiveFlowCard['step_state'], string> = {
    needs_action: 'Нужен шаг',
    checking: 'Проверяем',
    rejected: 'Нужно исправить',
    moderation: 'Модерация',
    waiting: 'Ожидание',
  };
  return labels[state];
}

function stateColor(state: ActiveFlowCard['step_state']): string {
  if (state === 'rejected') return P.danger;
  if (state === 'checking' || state === 'moderation') return P.warn;
  if (state === 'waiting') return P.jade;
  return P.terracotta;
}

function stepTitle(item: ActiveFlowCard): string {
  return item.current_checkpoint_display_name || flowStepLabel(item.current_checkpoint_kind);
}

interface FlowCardProps {
  item: ActiveFlowCard;
  onTap: (slotId: string) => void;
}

function FlowCard({ item, onTap }: FlowCardProps) {
  const pct = Math.min(100, Math.max(0, (item.current_step / item.total_steps) * 100));
  const accent = stateColor(item.step_state);

  return (
    <Card
      p={0}
      r={16}
      hover
      onClick={() => onTap(item.slot_id)}
      style={{
        width: 252,
        minWidth: 252,
        overflow: 'hidden',
        cursor: 'pointer',
        borderColor: 'rgba(60,50,35,0.12)',
      }}
    >
      <div style={{ display: 'flex', minHeight: 126 }}>
        <div style={{ position: 'relative', width: 94, flexShrink: 0, background: P.mist }}>
          {item.product_image_url ? (
            <img
              src={item.product_image_url}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                background: `linear-gradient(135deg, ${P.terraSoft}, ${P.jadeSoft})`,
              }}
            />
          )}
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              background: 'rgba(255,255,255,0.92)',
              color: S.label,
              padding: '3px 7px',
              borderRadius: 6,
              font: T.label,
              fontWeight: 700,
            }}
          >
            {item.marketplace}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0, padding: '10px 12px 11px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span
              style={{
                font: T.label,
                color: accent,
                fontWeight: 700,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {stateLabel(item.step_state)}
            </span>
            <Ic name="chevR" size={12} color={accent} style={{ marginLeft: 'auto' }} />
          </div>

          <p
            style={{
              font: T.headline,
              color: S.label,
              margin: '0 0 2px',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              minHeight: 44,
            } as React.CSSProperties}
          >
            {item.product_title || 'Задание'}
          </p>

          <p
            style={{
              font: T.caption,
              color: S.secondaryLabel,
              margin: '0 0 8px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {stepTitle(item)}
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                flex: 1,
                height: 4,
                borderRadius: 999,
                background: 'rgba(60,50,35,0.10)',
                overflow: 'hidden',
              }}
            >
              <div
                data-testid="flow-progress-fill"
                style={{
                  height: '100%',
                  width: `${pct}%`,
                  background: accent,
                  borderRadius: 999,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <span style={{ font: T.caption, color: accent, fontWeight: 700, flexShrink: 0 }}>
              {item.current_step}/{item.total_steps}
            </span>
          </div>

          <div style={{ marginTop: 7, font: T.caption, color: S.secondaryLabel }}>
            <span style={{ color: P.terraDark, fontWeight: 700 }}>
              +{formatKopecks(item.cashback_kopecks)}
            </span>{' '}
            вернётся
          </div>
        </div>
      </div>
    </Card>
  );
}

export function InProgressFlowsBlock() {
  const navigate = useNavigate();
  const { data, isPending, isError } = useQuery({
    queryKey: ['slots', 'active-flows'],
    queryFn: () => fetchActiveFlows(10),
    retry: false,
  });

  const items = data?.items ?? [];
  if (isPending || isError || items.length === 0) return null;

  return (
    <section aria-label="В прогрессе">
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 10,
        }}
      >
        <h2 style={{ font: T.title2, color: S.label, lineHeight: 1.1, margin: 0 }}>
          В прогрессе
        </h2>
        <span style={{ font: T.caption, color: S.secondaryLabel }}>
          {items.length} активн.
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 10,
          overflowX: 'auto',
          margin: '0 -16px',
          padding: '0 16px 4px',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
        }}
      >
        {items.map((item) => (
          <FlowCard key={item.slot_id} item={item} onTap={(slotId) => navigate('/flow/' + slotId)} />
        ))}
      </div>
    </section>
  );
}
